import { verifyKioskPinRequest } from 'api/requests/kiosk-session-requests';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import kioskSessionService from 'services/kiosk-session-service';
import {
  KIOSK__PIN_INVALID,
  KIOSK__PIN_PROMPT,
  KIOSK__PIN_VERIFICATION_EXPIRED,
  KIOSK__PIN_WELCOME,
} from 'translations/constants';
import { getInitials } from 'utils/helpers';
import KioskNumpad from './components/KioskNumpad';
import {
  KioskPinAvatar,
  KioskPinDot,
  KioskPinDotsRow,
  KioskPinFeedback,
  KioskPinPrompt,
  KioskPinWelcome,
  KioskStageCentered,
} from './styles';
import { useEndKioskSession } from './useEndKioskSession';

const PIN_LENGTH = 5;
const DIGITS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
const HOP_STAGGER_MS = 110;

const shuffleDigits = (): string[] => {
  const digits = [...DIGITS];
  for (let i = digits.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [digits[i], digits[j]] = [digits[j], digits[i]];
  }
  return digits;
};

interface KioskPinViewProps {
  employeeId: number;
  employeeName: string;
  /** True when this reopen was triggered by a 3-minute-expired verification token during clock-in/out. */
  expired?: boolean;
  onClose: () => void;
  onVerified: (pinVerificationToken: string) => void;
  /** VerifyPin came back with reason "NoPin" — the cached hasPin was stale; hand off to the notice. */
  onNoPin: () => void;
  /** Reports PIN-verify in-flight state so the persistent header can disable its close button. */
  onBusyChange: (busy: boolean) => void;
}

const KioskPinView = ({
  employeeId,
  employeeName,
  expired,
  onClose,
  onVerified,
  onNoPin,
  onBusyChange,
}: KioskPinViewProps) => {
  const [t] = useTranslation();
  const endSession = useEndKioskSession();
  const [digits, setDigits] = useState('');
  const [order, setOrder] = useState<string[]>(shuffleDigits);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    onBusyChange(verifying);
  }, [verifying, onBusyChange]);

  // Mount-only: the view remounts per stage entry thanks to stageKey.
  useEffect(() => {
    setDigits('');
    setError(false);
    setOrder(shuffleDigits());
  }, []);

  const submit = async (pin: string) => {
    const token = kioskSessionService.getToken();
    if (!token) {
      endSession();
      return;
    }
    setVerifying(true);
    try {
      const result = await verifyKioskPinRequest(token, employeeId, pin);
      if (result.valid && result.pinVerificationToken) {
        onVerified(result.pinVerificationToken);
        return;
      }
      if (result.reason === 'NoPin') {
        onNoPin();
        return;
      }
      setDigits('');
      setError(true);
      setOrder(shuffleDigits());
    } catch (e) {
      const httpStatus = (e as { response?: { status?: number } })?.response?.status;
      if (httpStatus === 401) {
        endSession();
        return;
      }
      setDigits('');
      setError(true);
      setOrder(shuffleDigits());
    } finally {
      setVerifying(false);
    }
  };

  const handleDigit = (digit: string) => {
    if (verifying || digits.length >= PIN_LENGTH) return;
    setError(false);
    const next = digits + digit;
    setDigits(next);
    if (next.length === PIN_LENGTH) {
      submit(next);
    }
  };

  const handleBackspace = () => {
    if (verifying) return;
    setDigits(prev => prev.slice(0, -1));
  };

  const handleClose = () => {
    if (verifying) return;
    onClose();
  };

  // Re-subscribes on every render so the closure always sees the latest digits/verifying state.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (verifying) return;
      if (/^[0-9]$/.test(event.key)) {
        handleDigit(event.key);
      } else if (event.key === 'Backspace') {
        handleBackspace();
      } else if (event.key === 'Escape') {
        handleClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  });

  const nameParts = employeeName.trim().split(/\s+/);
  const firstName = nameParts[0] ?? '';
  const initials = getInitials(firstName, nameParts[nameParts.length - 1] ?? '');

  let feedbackMessage = '';
  if (error) {
    feedbackMessage = t(KIOSK__PIN_INVALID);
  } else if (expired) {
    feedbackMessage = t(KIOSK__PIN_VERIFICATION_EXPIRED);
  }

  return (
    <KioskStageCentered>
      <KioskPinAvatar>{initials}</KioskPinAvatar>
      <KioskPinWelcome>
        {t(KIOSK__PIN_WELCOME)}, {employeeName}
      </KioskPinWelcome>
      <KioskPinPrompt>{t(KIOSK__PIN_PROMPT)}</KioskPinPrompt>

      <KioskPinDotsRow shaking={error}>
        {Array.from({ length: PIN_LENGTH }).map((_, index) => (
          <KioskPinDot
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            filled={index < digits.length}
            hopping={verifying}
            hopDelayMs={index * HOP_STAGGER_MS}
          />
        ))}
      </KioskPinDotsRow>

      <KioskPinFeedback>{feedbackMessage}</KioskPinFeedback>

      <KioskNumpad order={order} onDigit={handleDigit} onBackspace={handleBackspace} disabled={verifying} />
    </KioskStageCentered>
  );
};

KioskPinView.defaultProps = {
  expired: false,
};

export default KioskPinView;
